/**
 * Иконки валют: бочонок мёда ☾ (улья) и гранёный кристалл ✦ (кассы).
 *
 * Назначение файла: визуальный язык денег вместо знаков Юникода. В кегле
 * подписи ☾ неотличим от буквы «С», а ✦ читается как звёздочка сноски, и оба
 * знака зависят от шрифтов системы (docs/tasks/36-currency-icons.md).
 *
 * Форматтеры `@hive/i18n` и переводы при этом не меняются: строка по-прежнему
 * приходит со знаком, а картинку на его место подставляет `Crystals`.
 * Так знак остаётся единственным источником правды о валюте, а клиент только
 * рисует (закон 10).
 */

import { CRYSTAL_SYMBOL } from "@hive/i18n";
import { useState } from "react";

export type CrystalKind = keyof typeof CRYSTAL_SYMBOL;

/** Картинки лежат в `public/art` и раздаются статикой, как облики и Очаг. */
const COIN_ART: Record<CrystalKind, string> = {
  hive: "/art/coin-hive.webp",
  cash: "/art/coin-cash.webp",
};

export interface CrystalIconProps {
  readonly kind: CrystalKind;
  /** Размер в точках. Без него иконка меряется кеглем строки, в которой стоит. */
  readonly size?: number;
  readonly className?: string;
}

/**
 * Декоративная иконка: сумма рядом с ней озвучивается отдельно.
 *
 * Картинка может не докачаться в метро, и тогда на её место возвращается знак:
 * сумма без валюты хуже, чем сумма со знаком.
 */
export function CrystalIcon({
  kind,
  size,
  className,
}: CrystalIconProps): React.JSX.Element {
  const [broken, setBroken] = useState(false);
  const rootClass = ["crystal-icon", `crystal-icon--${kind}`, className]
    .filter(Boolean)
    .join(" ");

  if (broken) {
    return (
      <span className={`${rootClass} crystal-icon--sign`} aria-hidden="true">
        {CRYSTAL_SYMBOL[kind]}
      </span>
    );
  }

  return (
    <img
      className={rootClass}
      src={COIN_ART[kind]}
      alt=""
      aria-hidden="true"
      decoding="async"
      onError={() => setBroken(true)}
      {...(size === undefined
        ? {}
        : // Свой размер приходит стилем: правило класса задаёт размер в em и
          // атрибуты width и height перебивает
          { width: size, height: size, style: { width: size, height: size } })}
    />
  );
}

const SIGN_TO_KIND: ReadonlyMap<string, CrystalKind> = new Map([
  [CRYSTAL_SYMBOL.hive, "hive"],
  [CRYSTAL_SYMBOL.cash, "cash"],
]);

/** Пробел перед знаком уходит вместе с ним: отступ у иконки задаётся стилем. */
const SIGN_PATTERN = new RegExp(
  `[\\s\\u00A0]?([${CRYSTAL_SYMBOL.hive}${CRYSTAL_SYMBOL.cash}])`,
  "g",
);

export interface CrystalsProps {
  /** Готовая строка с суммой: результат formatCrystals или фраза с ним внутри. */
  readonly children: string;
}

/**
 * Строка с деньгами: знаки валют заменяются картинками, остальное остаётся как есть.
 *
 * Компонент принимает уже собранную фразу, а не число, потому что сумма
 * приходит и отдельным значением («12 ☾»), и внутри перевода («Награда: 12 ☾
 * и 12 опыта»). Разбирать готовую строку — единственный способ покрыть оба
 * случая, не трогая каталог переводов.
 */
export function Crystals({ children }: CrystalsProps): React.JSX.Element {
  const parts = children.split(SIGN_PATTERN);

  return (
    <>
      {parts.map((part, index) => {
        const kind = SIGN_TO_KIND.get(part);
        if (kind === undefined) {
          return part;
        }
        return (
          // Порядок частей строки неизменен, поэтому индекс здесь — честный ключ
          <span className="crystal-glyph" key={`${kind}-${String(index)}`}>
            <CrystalIcon kind={kind} />
            {/* Знак остаётся для экранного диктора: он читает сумму как раньше */}
            <i className="crystal-glyph__sign">{CRYSTAL_SYMBOL[kind]}</i>
          </span>
        );
      })}
    </>
  );
}

export interface CrystalAmountProps {
  readonly kind: CrystalKind;
  readonly amount: string;
  readonly size?: number;
}

/** Число с иконкой валюты — для превью обмена, где знака в строке нет. */
export function CrystalAmount({
  kind,
  amount,
  size,
}: CrystalAmountProps): React.JSX.Element {
  return (
    <span className={`crystal-amount crystal-amount--${kind}`}>
      <CrystalIcon kind={kind} {...(size === undefined ? {} : { size })} />
      <span className="crystal-amount__value">{amount}</span>
    </span>
  );
}
